export type GatedOutcome = "executed" | "skipped-not-owned";

export interface OwnershipGate<Session> {
	owns(session: Session): Promise<boolean>;
	commitIfOwned?<T>(session: Session, effect: () => Promise<T>): Promise<{ kind: "applied"; value: T } | { kind: "skipped-not-owned" }>;
}

/**
 * The gate check answers only "does this scope still hold the lease". An effect that runs its OWN
 * durable identity CAS can still be declined by a different active claim on the same row, and that
 * decline is indistinguishable from a lost lease as far as the caller's next decision goes. So an
 * effect MAY report its own "skipped-not-owned" verdict, which is surfaced verbatim; returning
 * nothing keeps the previous "executed" meaning (PR #3450 review / LSP-17735).
 *
 * An effect may also widen that vocabulary with verdicts of its own (see
 * SessionStore.markNeedsAttention's "already-complete"/"already-bytes-gone"): whatever it returns is
 * passed through untouched, so the caller can map the effect's REAL result rather than assuming
 * "the effect ran" means "the effect's intended verdict is now the row's state".
 */
export async function runGuardedEffect<Session, Outcome extends string = GatedOutcome>(
	gate: OwnershipGate<Session>,
	session: Session,
	effect: () => Promise<Outcome | void>
): Promise<Outcome | GatedOutcome> {
	// P-8: an app-backed writer lease validates the fencing token and commits
	// the effect in one IndexedDB transaction. Do not split that into an owns()
	// read followed by a separate write: an old epoch could win the gap.
	if (typeof gate.commitIfOwned === "function") {
		const result = await gate.commitIfOwned(session, effect);
		return result.kind === "skipped-not-owned" ? "skipped-not-owned" : (result.value ?? "executed");
	}
	if (!(await gate.owns(session))) return "skipped-not-owned";
	return (await effect()) ?? "executed";
}
